import React, { useEffect, useMemo, useRef } from "react";
import {
  Accent,
  FloatingBadge,
  HeroBadge,
  HeroContent,
  HeroEyebrow,
  HeroHighlights,
  HeroSubtitle,
  HeroTitle,
  HeroVisual,
  HighlightCard,
  HighlightLabel,
  HighlightMeta,
  HighlightTitle,
  Metric,
  MetricLabel,
  MetricValue,
  MetricsRow,
  Name,
  SocialTray,
  StyledButtonsContainer,
  StyledHome,
  VisualCard,
  VisualGlow,
} from "../../Styled/Home/Home.styled";
import { ResumeLink, StyledButton } from "../../Styled/Button/Buttom.style";
import { IconContext } from "react-icons/lib";
import { Socialicons } from "../../Styled/social/Socialfollow";
import anime from "animejs";
import { useLottie } from "lottie-react";
import animationData from "../../../src/assiat/laptopCoffe.json";
import { Typewriter } from "react-simple-typewriter";

const Homepage = () => {
  const nameRef = useRef(null);
  const heroTextRef = useRef([]);
  heroTextRef.current = [];

  const attachHeroTextRef = (element) => {
    if (element && !heroTextRef.current.includes(element)) {
      heroTextRef.current.push(element);
    }
  };

  useEffect(() => {
    if (!nameRef.current) return;
    anime({
      targets: nameRef.current,
      opacity: [0, 1],
      translateY: [16, 0],
      easing: "easeOutQuad",
      duration: 900,
      delay: 200,
    });
  }, []);

  useEffect(() => {
    if (!heroTextRef.current.length) return;
    const anim = anime({
      targets: heroTextRef.current,
      opacity: [0, 1],
      translateY: [12, 0],
      easing: "easeOutQuad",
      duration: 900,
      delay: anime.stagger(120),
    });
    return () => anim.pause();
  }, []);

  const options = {
    animationData: animationData,
    loop: true,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid meet",
    },
    width: 100,
    height: 200,
  };
  const { View } = useLottie(options);

  const bangladeshHour = useMemo(
    () =>
      Number(
        new Intl.DateTimeFormat("en-US", {
          hour: "2-digit",
          hour12: false,
          timeZone: "Asia/Dhaka",
        }).format(new Date())
      ),
    []
  );

  const bangladeshTime = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Dhaka",
      }).format(new Date()),
    []
  );

  const greeting =
    bangladeshHour < 12
      ? "Good morning"
      : bangladeshHour < 18
      ? "Good afternoon"
      : "Good evening";

  const metrics = [
    { value: "25+", label: "Production interfaces shipped" },
    { value: "4.9★", label: "Client satisfaction rating" },
    { value: "3x", label: "Average performance uplift" },
  ];

  const highlightItems = [
    {
      label: "Platform",
      title: "Logistics control tower",
      meta: "React · Zustand · Mapbox",
    },
    {
      label: "Growth",
      title: "Checkout uplift +37%",
      meta: "Experimentation · Web Vitals",
    },
    {
      label: "Open source",
      title: "Query automation kit",
      meta: "2.6k weekly installs",
    },
  ];

  return (
    <IconContext.Provider value={{ size: "1rem" }}>
      <StyledHome id="home">
        <HeroContent className="mt-20">
          <HeroEyebrow ref={attachHeroTextRef}>
            Full-stack website developer · Available for remote collaboration
          </HeroEyebrow>
          <Name ref={nameRef}>Enam Hossain</Name>
          <HeroTitle ref={attachHeroTextRef}>
            Full-Stack Website Developer specializing in the MERN Stack.
</HeroTitle>
<HeroSubtitle ref={attachHeroTextRef}>
            I deliver high-performance solutions across the entire product
            lifecycle, offering deep expertise in{" "}
  <Accent>
    <Typewriter
      words={[
                  "Frontend Development",
                  "WordPress Theme Customization",
                  "Shopify Maintenance",
      ]}
      loop={0}
      cursor
      cursorStyle="|"
      typeSpeed={70}
      deleteSpeed={50}
      delaySpeed={1500}
    />
  </Accent>{" "}
            with the MERN Stack to ship fast, reliable web applications that
            scale with your business.
</HeroSubtitle>

          <MetricsRow>
            {metrics.map((metric) => (
              <Metric key={metric.label}>
                <MetricValue>{metric.value}</MetricValue>
                <MetricLabel>{metric.label}</MetricLabel>
              </Metric>
            ))}
          </MetricsRow>

          <StyledButtonsContainer>
            <ResumeLink
              to="resume"
              smooth={true}
              duration={0}
              delay={0}
              spy={true}
              spyThrottle={0}
              exact="true"
              offset={-50}
              href="https://drive.google.com/file/d/11g5TeVJfQnM91C_akNT-dbrr4En4fFEy/view?usp=drive_link"
              target="_blank"
            >
              Resume
            </ResumeLink>
            <StyledButton
              secondary="true"
              content="B"
              to="about"
              smooth={true}
              duration={0}
              delay={0}
              spy={true}
              spyThrottle={0}
              exact="true"
              offset={-50}
            >
              <span>Contact</span>
            </StyledButton>
            <HeroBadge aria-live="polite">
              {greeting}! It&apos;s {bangladeshTime} in Dhaka.
            </HeroBadge>
          </StyledButtonsContainer>

          <SocialTray>
            <span>Let&apos;s connect</span>
            <Socialicons />
          </SocialTray>
        </HeroContent>

        <HeroVisual>
          <VisualGlow />
          <VisualCard>{View}</VisualCard>
          <FloatingBadge>
            <p>Currently shipping</p>
            <strong>Enterprise analytics suite</strong>
            <span>React 18 · Micro-frontends</span>
          </FloatingBadge>
          <HeroHighlights>
            {highlightItems.map((item) => (
              <HighlightCard key={item.title}>
                <HighlightLabel>{item.label}</HighlightLabel>
                <HighlightTitle>{item.title}</HighlightTitle>
                <HighlightMeta>{item.meta}</HighlightMeta>
              </HighlightCard>
            ))}
          </HeroHighlights>
        </HeroVisual>
      </StyledHome>
    </IconContext.Provider>
  );
};

export default Homepage;

